import classNames from 'classnames';
import Image from 'next/image';
import { useEffect } from 'react';

import Buttons from '@/components/Buttons';
import FileUploadIndicator from '@/components/FileUploadIndicator';
import NoodleAI from '@/components/Icons/NoodleAI';
import XCircle from '@/components/Icons/XCircle';
import TextEllipsis from '@/components/TextEllipsis';
import { mixpanelTrack } from '@/providers/Mixpanel';
import { type SocketMessageType } from '@/providers/Socket';
import useSocketContext from '@/providers/Socket/useSocketContext';

import { type Job, JobStatus, JobType } from '../../Context';
import useJobContext from '../../useJobContext';
import MediaIcon from '../MediaIcon';
import StatusIcon from '../StatusIcon';

import s from './AsyncJob.module.scss';

type AsyncJobProps = {
  job: Job;
  isSmall?: boolean;
};

const typesWithProgress = [JobType.MESSAGE_MEDIA_UPLOAD, JobType.TEST_JOB];
const statusWithRetry = [JobStatus.CANCELED, JobStatus.FAILED];

export default function AsyncJob({ job, isSmall }: AsyncJobProps): JSX.Element {
  const { jobs, removeJob } = useJobContext();
  const { status, progress, dependsOn, type, mediaType, title } = job;

  const {
    addListener: addSocketListener,
    removeListener: removeSocketListener,
    isInitialized: isSocketInitialized,
    joinChannels,
  } = useSocketContext();

  useEffect(() => {
    if (job.type === JobType.AI_JOB && isSocketInitialized && job.correlationId) {
      joinChannels([job.correlationId]);
    }
  }, [joinChannels, job.correlationId, isSocketInitialized, job.type]);

  useEffect(() => {
    if (job.type !== JobType.AI_JOB) return () => {};
    const listenerId = addSocketListener({
      fn: () => removeJob(job),
      messageType: job.metadata?.messageType as SocketMessageType,
    });

    return () => removeSocketListener(listenerId);
  }, [addSocketListener, removeSocketListener, job, removeJob]);

  const cancelJob = (): void => {
    mixpanelTrack('Async Job Cancel pressed', {
      ...(job.metadata || {}),
      correlationId: job.correlationId,
      type,
    });
    job.cancel();
    removeJob(job);
  };

  const retryJob = (): void => {
    mixpanelTrack('Async Job Retry pressed', {
      ...(job.metadata || {}),
      correlationId: job.correlationId,
      type,
    });
    job.retry();
    setTimeout(() => {
      const parentJob = jobs.find((j) => j.dependsOn?.some((i) => i.id === job.id));
      if (parentJob) {
        parentJob.retry();
      }
    }, 1000);
  };

  const dependingJobs = jobs
    .filter(({ id }) => dependsOn?.map(({ id: dependId }) => dependId).includes(id))
    .filter((j) => (job.type === JobType.MESSAGE_MEDIA_UPLOAD ? j.type !== JobType.MESSAGE_MEDIA_UPLOAD : true));

  const getJobTitle = (): string => {
    let jobTitle = title;
    if (type === JobType.AI_JOB) {
      jobTitle = `Glade AI is working on ${jobTitle}...`;
      return jobTitle;
    }
    if (type === JobType.AI_LIST_AGENT) {
      jobTitle = `${jobTitle} AI agent is running...`;
      return jobTitle;
    }
    if (typesWithProgress.includes(type)) {
      if (status === JobStatus.IN_PROGRESS) {
        jobTitle += ' processing';
      }
      if (status === JobStatus.COMPLETED) {
        jobTitle += ' completed';
      }
      if (status === JobStatus.CANCELED) {
        jobTitle += ' processing canceled';
      }
      if (status === JobStatus.FAILED) {
        jobTitle += ' processing failed';
      }
    } else {
      if (status === JobStatus.IN_PROGRESS) {
        jobTitle += ' processing...';
      }
      if (status === JobStatus.COMPLETED) {
        jobTitle += ' completed';
      }
      if (status === JobStatus.CANCELED) {
        jobTitle += ' canceled';
      }
      if (status === JobStatus.FAILED) {
        jobTitle += ' failed';
      }
    }
    return jobTitle;
  };

  const isAIJob = job.type === JobType.AI_JOB || job.type === JobType.AI_LIST_AGENT;

  return (
    <div className={classNames(s.container, { [s['container--small']]: isSmall, [s['container--ai']]: isAIJob })}>
      <div className={classNames('body-sm', s.top)}>
        {(typesWithProgress.includes(type) && mediaType && (
          <>
            {mediaType.includes('image') && status === JobStatus.COMPLETED && job.uploadMedia?.url ? (
              <Image
                src={job.uploadMedia.url}
                alt=''
                width={24}
                height={24}
                style={{ backgroundColor: 'var(--color-gray-25)', borderRadius: 4, objectFit: 'cover' }}
              />
            ) : (
              <MediaIcon type={mediaType} />
            )}
          </>
        )) ||
          (!isAIJob && <StatusIcon status={status} />)}
        {isAIJob ? <NoodleAI size={24} /> : ''}
        <div
          className={classNames(
            'caption',
            s.title,
            { [s['title--strong']]: status !== JobStatus.IN_PROGRESS },
            { [s['title--error']]: status === JobStatus.FAILED },
          )}
        >
          <TextEllipsis isNotExpandable className={isAIJob ? 'ai-label' : undefined}>
            {getJobTitle()}
          </TextEllipsis>
          {status === JobStatus.IN_PROGRESS && typesWithProgress.includes(type) && (
            <FileUploadIndicator className={s.progress} showTop={false} uploadPercentage={progress} />
          )}
        </div>
        {statusWithRetry.includes(status) && (
          <Buttons isWrapper className={s.retry} onClick={retryJob}>
            Try again
          </Buttons>
        )}
        {(status === JobStatus.IN_PROGRESS || status === JobStatus.FAILED) &&
          !isAIJob &&
          !dependingJobs.filter((dj) => dj.status === JobStatus.IN_PROGRESS).length && (
            <Buttons
              isExtraSmall
              onClick={cancelJob}
              className={s.cancel}
              icon={typesWithProgress.includes(type) ? <XCircle weight='fill' /> : undefined}
            >
              Cancel
            </Buttons>
          )}
      </div>
      {dependingJobs.map((subJob) => (
        <AsyncJob key={subJob.id} job={subJob} isSmall />
      ))}
    </div>
  );
}
